import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';

import { Card, CardContent } from './ui/card';
import { Calendar, Eye, Home } from 'lucide-react';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Address, formatAddressUrl, formatTime } from '@/lib/utils';

type AppointmentTableRow = AppointmentTable & {
  projects?: {
    name: string;
    users: {
      last_name: string;
      first_name: string;
      phone_number: string;
    };
  };
};

const columnHelper = createColumnHelper<AppointmentTableRow>();

export const AppointmentsTable = ({ data }: { data: AppointmentTableRow[] }) => {
  const { t } = useTranslation();
  const columns = [
    columnHelper.accessor('appointment_time', {
      id: 'appointment_hour',
      header: () => (
        <TableHead className="w-[120px] md:w-[180px]">{t('startTime')}</TableHead>
      ),
      cell: (info) => {
        return (
          <TableCell>
            <span className="inline-flex items-center flex-wrap">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="ml-2 text-base ">{formatTime(info.getValue() ?? '')}</span>
            </span>
          </TableCell>
        );
      },
    }),
    columnHelper.accessor('end_date', {
      id: 'end_date',
      header: () => (
        <TableHead className="w-[120px] md:w-[180px]">{t('endTime')}</TableHead>
      ),
      cell: (info) => {
        const val = info.getValue();
        if (!val) return <TableCell></TableCell>;
        return (
          <TableCell>
            <span className="inline-flex items-center flex-wrap">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="ml-2 text-base ">{formatTime(info.getValue() ?? '')}</span>
            </span>
          </TableCell>
        );
      },
    }),
    columnHelper.display({
      id: 'client_name',
      header: () => <TableHead>{}</TableHead>,
      cell: (info) => {
        const original = info.row.original;
        let content = 'No client';
        if (original.projects) {
          const { users } = original.projects;
          if (users?.first_name) {
            content = `${users?.first_name} ${users?.last_name}`;
          } else {
            content = '';
          }
        }
        return <TableCell> {content}</TableCell>;
      },
    }),
    columnHelper.accessor('status', {
      header: () => <TableHead className="hidden md:table-cell">{t('status')}</TableHead>,

      cell: (info) => (
        <TableCell className="hidden md:table-cell">{info.getValue()} </TableCell>
      ),
    }),
    columnHelper.accessor('scope', {
      header: () => <TableHead>{t('scope')}</TableHead>,
      cell: (info) => <TableCell>{info.getValue()}</TableCell>,
    }),
    columnHelper.accessor('appointment_id', {
      id: 'action',
      header: () => <TableHead>{t('action')}</TableHead>,
      cell: (i) => {
        return (
          <TableCell>
            <Link
              className="inline-flex gap-2 items-center"
              to={'/appointment/' + i.getValue()}
            >
              <Eye className="h-4 w-4" />
              <span className="">{t('view')}</span>
            </Link>
          </TableCell>
        );
      },
    }),
  ];
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });
  const isSmallDevice = useMediaQuery('only screen and (max-width : 768px)');
  const isMediumDevice = useMediaQuery(
    'only screen and (min-width : 769px) and (max-width : 992px)'
  );

  if (isSmallDevice || isMediumDevice) {
    return <AppointmentCards data={data} />;
  }

  return (
    <>
      <div className="w-full">
        <Table className="">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <React.Fragment key={header.id}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </React.Fragment>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <React.Fragment key={row.id}>
                <TableRow>
                  {row.getVisibleCells().map((cell) => (
                    <React.Fragment key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </React.Fragment>
                  ))}
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
};
export const AppointmentCards = ({ data }: { data: AppointmentTableRow[] }) => {
  function getClientInitials(row: AppointmentTableRow) {
    const { projects } = row;
    if (!projects) return '';
    const { users } = projects;
    if (!users?.first_name) return '';
    return `${users.first_name[0]}${users.last_name[0]}`;
  }
  const { t } = useTranslation();
  function getClientName(row: AppointmentTableRow) {
    const { projects } = row;
    if (!projects) return '';
    const { users } = projects;
    if (!users?.first_name) return '';
    return `${users.first_name} ${users.last_name}`;
  }
  function getBadgeClass(status: string) {
    switch (status) {
      case 'Scheduled':
        return 'bg-yellow-50 text-yellow-600 border-yellow-100';
      case 'Completed':
        return 'bg-green-50 text-green-600 border-green-100';
      default:
        return 'bg-gray-50 text-gray-600 border-gray-100';
    }
  }
  return (
    <div className="w-full">
      {data.map((row) => (
        <Card key={row.appointment_id} className="my-2">
          <CardContent className="p-4">
            <div className="grid gap-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-xs text-muted-foreground" />
                  <span className="font-medium">
                    {formatTime(row.appointment_time || '')}
                  </span>
                  {row.end_date && (
                    <>
                      <span className="text-muted-foreground"> - </span>
                      <span className="font-medium">
                        {formatTime(row.end_date || '')}
                      </span>
                    </>
                  )}
                </div>
                <Badge
                  variant="outline"
                  className={`text-xs ${getBadgeClass(row.status!)} rounded-full`}
                >
                  {t((row.status || '').replace(/\s/g, '').toLowerCase())}
                </Badge>
              </div>
              <div>
                <h3 className="font-semibold mb-1">{t('client')}</h3>
                <div className="flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src="/placeholder.svg" alt="Emily Johnson" />
                    <AvatarFallback>{getClientInitials(row)}</AvatarFallback>
                  </Avatar>
                  <span>{getClientName(row)}</span>
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-1">{t('address')}</h3>
                <a
                  className="hover:underline text-sm inline-flex items-center gap-2 text-muted-foreground"
                  href={formatAddressUrl({
                    street: row.street,
                    city: row.city,
                    state: row.state,
                    zip_code: row.zip_code,
                  } as Address)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Home className="h-4 w-4 self-start" />
                  <span>
                    {row.street}
                    <br />
                    {row.city}, {row.state}
                    <br /> {row.zip_code}
                  </span>
                </a>
              </div>
              <div>
                <h3 className="font-semibold mb-1">{t('scope')}</h3>
                <p className="text-sm text-muted-foreground">{row.scope}</p>
              </div>
              <Link
                className="inline-flex ml-auto justify-end underline items-center"
                to={'/appointment/' + row.appointment_id}
              >
                <Eye className="h-4 w-4 mr-2" />
                <span className="">{t('view')}</span>
              </Link>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
